import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import FeedbackDetailModal from './FeedbackDetailModal';
import { FEEDBACK_STATUS_LABELS } from '../../types/feedback.types';
import type { FeedbackItemAdmin } from '../../types/feedback.types';

const FeedbackManagementTab: React.FC = () => {
    const { t } = useTranslation('admin');
    const queryClient = useQueryClient();
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItemAdmin | null>(null);

    const { data, isLoading, isError, refetch } = useQuery(
        ['admin-feedback', typeFilter, statusFilter],
        () => api.feedback.getFeedbackList({
            ...(typeFilter ? { type: typeFilter } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            limit: 100,
        }),
        { staleTime: 30000 }
    );

    const feedbackList: FeedbackItemAdmin[] = data?.feedback || [];

    const handleModalClose = () => {
        setSelectedFeedback(null);
        // Cache invalidation is handled by FeedbackDetailModal onSuccess
    };

    if (isError) {
        return (
            <div className="admin-section">
                <h2>{t('feedback.title', 'Feedback Management')}</h2>
                <p style={{ color: '#ef4444' }}>Failed to load feedback. Please try refreshing.</p>
            </div>
        );
    }

    return (
        <div className="admin-section">
            <h2>{t('feedback.title', 'Feedback Management')}</h2>
            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                {t('feedback.description', 'View, filter, and respond to user feedback.')}
            </p>

            <div className="feedback-admin-filters">
                <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                >
                    <option value="">{t('feedback.filters.allTypes', 'All Types')}</option>
                    <option value="bug">{t('feedback.types.bug', 'Bug Report')}</option>
                    <option value="feature">{t('feedback.types.feature', 'Feature Request')}</option>
                    <option value="general">{t('feedback.types.general', 'General')}</option>
                </select>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="">{t('feedback.filters.allStatuses', 'All Statuses')}</option>
                    {Object.entries(FEEDBACK_STATUS_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                    ))}
                </select>
            </div>

            {isLoading ? (
                <div className="feedback-loading">{t('feedback.loading', 'Loading feedback...')}</div>
            ) : feedbackList.length === 0 ? (
                <div className="feedback-admin-empty">
                    <p>{t('feedback.noResults', 'No feedback found.')}</p>
                </div>
            ) : (
                <table className="feedback-admin-table">
                    <thead>
                        <tr>
                            <th>{t('feedback.table.type', 'Type')}</th>
                            <th>{t('feedback.table.title', 'Title')}</th>
                            <th>{t('feedback.table.submittedBy', 'Submitted By')}</th>
                            <th>{t('feedback.table.rating', 'Rating')}</th>
                            <th>{t('feedback.table.status', 'Status')}</th>
                            <th>{t('feedback.table.date', 'Date')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {feedbackList.map((item) => (
                            <tr
                                key={item.id}
                                tabIndex={0}
                                onClick={() => setSelectedFeedback(item)}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSelectedFeedback(item)}
                            >
                                <td>
                                    <span className={`feedback-type-badge ${item.type}`}>{item.type}</span>
                                </td>
                                <td>{item.title}</td>
                                <td>{item.submitted_by}</td>
                                <td>
                                    {item.rating
                                        ? '\u2605'.repeat(item.rating) + '\u2606'.repeat(5 - item.rating)
                                        : '\u2014'}
                                </td>
                                <td>
                                    <span className={`feedback-status-badge ${item.status}`}>
                                        {FEEDBACK_STATUS_LABELS[item.status] || item.status}
                                    </span>
                                </td>
                                <td>{new Date(item.created_at).toLocaleDateString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {selectedFeedback && (
                <FeedbackDetailModal
                    feedback={selectedFeedback}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};

export default FeedbackManagementTab;
